import { useCallback } from 'react';
import Swal from 'sweetalert2';

/**
 * Converts a validation path to a human-readable label
 * @param {string} path - The validation path string
 * @returns {string} A formatted string for user display
 */
export const prettifyPath = (path) => {
    if (!path) return "";

    // Order ID
    if (path === 'orderId') {
        return 'Order ID';
    }

    // Order property fields
    if (path.startsWith('order.')) {
        const field = path.replace('order.', '');

        if (field === 'propertyId') return 'Property';
        if (field === 'buildingName') return 'Building Name';
        if (field === 'vendorId') return 'Vendor';
        if (field === 'deliveryStatus') return 'Delivery Status';
        if (field === 'comments') return 'Comments';
    }

    // Ordered items - make more user-friendly
    const orderedItem = path.match(/^order\.orderedItems\[(\d+)\]\.(.+)$/);
    if (orderedItem) {
        const itemIndex = +orderedItem[1] + 1;
        const field = orderedItem[2];

        const fieldMap = {
            'item_id': 'Item',
            'requestedQuantity': 'Quantity',
            'requestsSupported': 'Request Type',
            'price': 'Price',
            'jobName': 'Job Details'
        };

        const fieldName = fieldMap[field] || field;
        return `${getOrdinalNumber(itemIndex)} Ordered Item's ${fieldName}`;
    }

    const directOrderedItem = path.match(/^orderedItems\[(\d+)\]\.(.+)$/);
    if (directOrderedItem) {
        const itemIndex = +directOrderedItem[1] + 1;
        const field = directOrderedItem[2];

        const fieldMap = {
            'item_id': 'Item',
            'requestedQuantity': 'Quantity',
            'requestsSupported': 'Request Type',
            'price': 'Price',
            'jobName': 'Job Details'
        };

        const fieldName = fieldMap[field] || field;
        return `${getOrdinalNumber(itemIndex)} Ordered Item's ${fieldName}`;
    }

    // Ordered items array validation
    if (path === 'order.orderedItems' || path === 'orderedItems') {
        return 'Ordered Items';
    }

    // Fallback - capitalize and format the path
    return path
        .split('.')
        .join(' - ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .replace(/_/g, ' ');
};

/**
 * Converts numbers to ordinal words (1st, 2nd, 3rd, etc.)
 * @param {number} num - The number to convert
 * @returns {string} The ordinal representation
 */
const getOrdinalNumber = (num) => {
    const ordinals = {
        1: 'First',
        2: 'Second',
        3: 'Third',
        4: 'Fourth',
        5: 'Fifth',
        6: 'Sixth',
        7: 'Seventh',
        8: 'Eighth',
        9: 'Ninth',
        10: 'Tenth'
    };

    return ordinals[num] || `${num}th`;
};

/**
 * Formats validation errors recursively
 * @param {Object} errors - The validation error object
 * @param {string} prefix - The current path prefix
 * @returns {string[]} An array of formatted error messages
 */
const formatErrors = (errors, prefix = '') => {
    const errorList = [];

    Object.entries(errors).forEach(([key, value]) => {
        const fieldName = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            const prettyField = prettifyPath(fieldName);
            const friendlyMessage = makeFriendlyMessage(prettyField, value);
            errorList.push(`• ${friendlyMessage}`);
        } else if (Array.isArray(value)) {
            value.forEach((item, index) => {
                if (typeof item === 'object' && item !== null) {
                    const nestedErrors = formatErrors(item, `${fieldName}[${index}]`);
                    errorList.push(...nestedErrors);
                } else if (typeof item === 'string') {
                    const prettyField = prettifyPath(`${fieldName}[${index}]`);
                    const friendlyMessage = makeFriendlyMessage(prettyField, item);
                    errorList.push(`• ${friendlyMessage}`);
                }
            });
        } else if (typeof value === 'object' && value !== null) {
            const nestedErrors = formatErrors(value, fieldName);
            errorList.push(...nestedErrors);
        }
    });

    return errorList;
};

/**
 * Converts technical validation messages to user-friendly ones
 * @param {string} field - The field name
 * @param {string} message - The validation message
 * @returns {string} A user-friendly message
 */
const makeFriendlyMessage = (field, message) => {
    const formattedField = `<span class="fw-bold text-black">${field}</span>`;

    // Handle "required" or missing field errors
    if (message.toLowerCase().includes('required') || message.toLowerCase().includes('missing')) {
        // Check for specific ordered item fields first (more specific)
        if (field.toLowerCase().includes("ordered item's item")) {
            return `Please select an item for the ${formattedField}.`;
        }
        if (field.toLowerCase().includes("ordered item's quantity")) {
            return `Please specify the ${formattedField} for this item.`;
        }
        if (field.toLowerCase().includes("ordered item's job details")) {
            return `Please provide the ${formattedField} for this item.`;
        }
        if (field.toLowerCase().includes("ordered item's request type")) {
            return `Please select a request type for the ${formattedField}.`; // Updated for requestsSupported
        }
        if (field.toLowerCase().includes("ordered item's")) {
            return `Please provide the ${formattedField} to proceed with your order.`;
        }

        // Then check for the general ordered items array (less specific)
        if (field.toLowerCase() === 'ordered items') {
            return `Please add at least one item to your order.`;
        }

        // Other specific field checks
        if (field.toLowerCase().includes('vendor')) {
            return `Please select a ${formattedField} for your order.`;
        }
        if (field.toLowerCase().includes('property')) {
            return `Please choose a ${formattedField} for delivery.`;
        }

        return `The ${formattedField} field is required to continue.`;
    }

    // Handle invalid format errors
    if (message.toLowerCase().includes('invalid')) {
        if (field.toLowerCase().includes("ordered item's item")) {
            return `The ${formattedField} you selected is invalid. Please choose a valid item.`;
        }
        if (field.toLowerCase().includes("ordered item's request type")) {
            return `The ${formattedField} is invalid. Please select a valid request type.`; // Updated for requestsSupported
        }
        if (field.toLowerCase().includes('price')) {
            return `The ${formattedField} format is incorrect. Please enter a valid number (e.g., 29.99).`;
        }
        if (field.toLowerCase().includes('order id') || field.toLowerCase().includes('property id') || field.toLowerCase().includes('vendor id')) {
            return `The ${formattedField} is not valid. Please enter a correct ID.`;
        }
        return `The ${formattedField} format is incorrect. Please check and try again.`;
    }

    // Handle minimum value errors
    if (message.toLowerCase().includes('minimum') || message.toLowerCase().includes('min')) {
        if (field.toLowerCase().includes("ordered item's quantity")) {
            return `The ${formattedField} must be at least 1. Please adjust the value.`;
        }
        if (field.toLowerCase().includes('quantity')) {
            return `The ${formattedField} must be at least 1. Please adjust the value.`;
        }
        if (field.toLowerCase().includes('price')) {
            return `The ${formattedField} cannot be negative. Please enter a valid amount.`;
        }
        if (field.toLowerCase() === 'ordered items') {
            return `Please include at least one item in the ${formattedField}.`;
        }
        return `The ${formattedField} is below the minimum allowed value.`;
    }

    // Handle maximum value errors
    if (message.toLowerCase().includes('maximum') || message.toLowerCase().includes('max')) {
        if (field.toLowerCase().includes('quantity')) {
            return `The ${formattedField} exceeds the maximum allowed. Please reduce the value.`;
        }
        if (field.toLowerCase().includes('price')) {
            return `The ${formattedField} is too high. Please enter a lower amount.`;
        }
        return `The ${formattedField} exceeds the maximum allowed value.`;
    }

    // Handle specific errors for ordered items
    if (field.toLowerCase().includes("ordered item's item")) {
        return `Please select an item for the ${formattedField}.`;
    }
    if (field.toLowerCase().includes("ordered item's request type")) {
        return `Please select a request type for the ${formattedField}.`; // Updated for requestsSupported
    }
    if (field.toLowerCase().includes("ordered item's job details")) {
        return `Please provide job details for the ${formattedField}.`;
    }
    if (field.toLowerCase().includes("ordered item's quantity")) {
        return `Please enter a whole number for the ${formattedField}.`;
    }

    // Default fallback for unrecognized errors
    return `There's an issue with the ${formattedField}. Please review and correct it.`;
};

/**
 * Custom hook for validating form with enhanced error display
 * @param {Object} formik - The formik instance
 * @returns {Function} Validation function that returns boolean
 */
export const useValidateAndShowErrors = (formik) => {
    return useCallback(async () => {
        await formik.validateForm();

        if (!formik.isValid) {
            const errorMessages = formatErrors(formik.errors);

            const errors = errorMessages.join("<br/>");

            if (errors) {
                await Swal.fire({
                    icon: "warning",
                    title: "Let's Complete Your Order",
                    html: `
            <p style="margin-bottom: 15px; color: #666; font-size: 16px;">A few details need your attention to proceed:</p>
            <div style="text-align: left; background: #f8f9fa; padding: 15px; border-radius: 8px; border-left: 4px solid #ffc107; line-height: 1.6;">${errors}</div>`,
                    width: 600,
                    confirmButtonText: "I'll Fix These",
                    confirmButtonColor: "#007bff",
                });
            }

            return false;
        }

        return true;
    }, [formik]);
};

/**
 * Formats validation errors for display in an Alert component
 * @param {Object} errors - The validation error object
 * @returns {string[]} An array of formatted error messages
 */
export const formatValidationErrors = (errors) => {
    return Object.entries(errors).map(([field, message]) => {
        const prettyField = prettifyPath(field);
        return `${prettyField}: ${message}`;
    });
};